import React, { useState, useEffect, useRef, useCallback } from 'react';
import { assetsAPI } from '../../services/api';

const SymbolSearch = ({ value, onChange, assetClass, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search — filters by asset class when provided
  const doSearch = useCallback((q) => {
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    assetsAPI.search(q, 20, assetClass)
      .then((res) => setResults(res.data || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [assetClass]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (searchTerm) {
      debounceRef.current = setTimeout(() => doSearch(searchTerm), 300);
    } else {
      setResults([]);
    }
    return () => clearTimeout(debounceRef.current);
  }, [searchTerm, doSearch]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    onChange(item.symbol, item.name);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setIsOpen(true);
  };

  // Display: search term while typing, selected value when closed
  const displayValue = isOpen ? searchTerm : (value || '');

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <input
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        placeholder="Search symbol or company..."
        className="w-full px-4 py-2 bg-accent/10 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus-visible:ring-ring"
      />

      {isOpen && (
        <div className="absolute z-10 w-full mt-2 bg-card border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-sm text-muted-foreground">Searching...</div>
          )}
          {!loading && searchTerm && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">No results found</div>
          )}
          {!loading && !searchTerm && (
            <div className="px-4 py-3 text-sm text-muted-foreground">Type to search tickers...</div>
          )}
          {results.map((item) => (
            <button
              key={item.symbol}
              type="button"
              onClick={() => handleSelect(item)}
              className="w-full px-4 py-3 text-left hover:bg-accent/10 transition-colors border-b border-border last:border-b-0"
            >
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-bold text-foreground">{item.symbol}</div>
                  <div className="text-xs text-muted-foreground">{item.name}</div>
                </div>
                <div className="flex items-center gap-2">
                  {item.exchange && (
                    <span className="text-xs text-muted-foreground">{item.exchange}</span>
                  )}
                  {value === item.symbol && (
                    <span className="text-primary">&#10003;</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SymbolSearch;
